import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Rocket, Cog, Zap, Calendar, ExternalLink, Loader2, ArrowRight,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

import useDocumentTitle from "../hooks/useDocumentTitle";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// Customer-facing "What's New" — public, no auth. Curated subset of
// PRODUCT_WINS filtered through the backend's PUBLIC_WHATS_NEW_TAGS
// allowlist so internal A/B-winner promotions and admin-tooling launches
// stay private.
const TAG_META = {
  feature:     { color: "#1FDD83", icon: Sparkles, label: "FEATURE"     },
  integration: { color: "#22D3EE", icon: Cog,      label: "INTEGRATION" },
  developer:   { color: "#22D3EE", icon: Rocket,   label: "DEVELOPER"   },
  billing:     { color: "#FB923C", icon: Zap,      label: "BILLING"     },
  ux:          { color: "#A78BFA", icon: Sparkles, label: "POLISH"      },
};

function tagInfo(tag) {
  return TAG_META[tag] || { color: "#9CAEC9", icon: Sparkles, label: (tag || "UPDATE").toUpperCase() };
}

export default function WhatsNew() {
  useDocumentTitle("What's New — QorTrace");
  const [items, setItems]   = useState(null);
  const [error, setError]   = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetch(`${API}/api/whats-new?limit=40`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => setItems(d.items || []))
      .catch(() => setError("Could not load updates. Please try refreshing."));
  }, []);

  const filterOptions = [
    { key: "all",         label: "All updates" },
    { key: "feature",     label: "Features"    },
    { key: "developer",   label: "Developer"   },
    { key: "billing",     label: "Pricing"     },
  ];
  const visible = !items
    ? null
    : filter === "all"
      ? items
      : items.filter((i) => i.tag === filter);

  return (
    <div className="min-h-screen qt-radial-bg">
      <Header />
      <main className="max-w-[1100px] mx-auto px-6 sm:px-8 py-10" data-testid="whatsnew-page">
        <section className="mb-10">
          <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>
            WHAT'S NEW · QORTRACE
          </div>
          <h1 className="qt-h2 text-4xl sm:text-5xl mt-2" data-testid="whatsnew-title">
            Recent platform updates
          </h1>
          <p
            className="mt-3 max-w-2xl text-base"
            style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}
          >
            New features, integrations, and pricing changes — the customer-meaningful changes
            we release. Subscribe to platform updates from your{" "}
            <Link to="/account" className="underline" style={{ color: "var(--qt-cyan)" }}>
              account preferences
            </Link>{" "}
            once you're signed in.
          </p>
        </section>

        <div className="flex flex-wrap gap-2 mb-8" data-testid="whatsnew-filter-bar">
          {filterOptions.map((opt) => (
            <button
              key={opt.key}
              onClick={() => setFilter(opt.key)}
              data-testid={`whatsnew-filter-${opt.key}`}
              className="rounded-full px-4 py-1.5 text-xs uppercase tracking-wider transition"
              style={{
                fontFamily: "monospace",
                letterSpacing: "0.12em",
                border: filter === opt.key
                  ? "1px solid var(--qt-cyan)"
                  : "1px solid rgba(255,255,255,0.14)",
                background: filter === opt.key
                  ? "rgba(0,229,255,0.12)"
                  : "transparent",
                color: filter === opt.key ? "var(--qt-cyan)" : "var(--qt-text-2)",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {error && (
          <div
            className="rounded-lg border p-4 text-sm"
            style={{
              borderColor: "rgba(255,90,110,0.35)",
              background:  "rgba(255,90,110,0.08)",
              color:       "#FF5A6E",
            }}
            data-testid="whatsnew-error"
          >
            {error}
          </div>
        )}

        {!visible && !error && (
          <div className="flex items-center gap-3 opacity-70" data-testid="whatsnew-loading">
            <Loader2 size={16} className="animate-spin" /> Loading updates…
          </div>
        )}

        {visible && visible.length === 0 && (
          <div
            className="rounded-lg border p-6 text-sm opacity-80"
            style={{
              borderColor: "rgba(255,255,255,0.10)",
              background:  "rgba(255,255,255,0.02)",
            }}
            data-testid="whatsnew-empty"
          >
            No entries match this filter yet. Check back soon — we release updates every week.
          </div>
        )}

        {visible && visible.length > 0 && (
          <ol className="relative" data-testid="whatsnew-feed">
            <div
              className="absolute left-[7px] top-0 bottom-0"
              style={{ width: 2, background: "rgba(0,229,255,0.16)" }}
              aria-hidden
            />
            {visible.map((it, idx) => (
              <Entry key={it.id} entry={it} first={idx === 0} />
            ))}
          </ol>
        )}

        {/* Bottom upgrade nudge — soft "follow the platform" CTA. */}
        <div
          className="mt-12 qt-card text-center"
          style={{
            background: "linear-gradient(135deg, rgba(0,229,255,0.06), rgba(0,229,255,0.02))",
            borderColor: "rgba(0,229,255,0.3)",
          }}
        >
          <div className="qt-label" style={{ color: "var(--qt-cyan)" }}>STAY AHEAD OF THE QUANTUM CURVE</div>
          <h3 className="text-2xl font-semibold mt-2" style={{ color: "#fff" }}>
            Run a free wallet scan in 90 seconds
          </h3>
          <p className="mt-2 text-sm max-w-xl mx-auto" style={{ color: "var(--qt-text-2)" }}>
            See the platform live — drop in a wallet address and get an instant post-quantum
            exposure score across 18 chains. No signup required.
          </p>
          <div className="mt-5 flex flex-wrap gap-3 justify-center">
            <Link to="/scan" className="qt-btn qt-btn-primary" data-testid="whatsnew-cta-scan">
              Run a free scan <ArrowRight size={14} />
            </Link>
            <Link to="/pricing" className="qt-btn qt-btn-secondary" data-testid="whatsnew-cta-pricing">
              See pricing
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Entry({ entry, first }) {
  const meta = tagInfo(entry.tag);
  const Icon = meta.icon;
  return (
    <li className="relative pl-10 pb-8" data-testid={`whatsnew-entry-${entry.id}`}>
      <div
        className="absolute left-0 top-1.5 flex h-4 w-4 items-center justify-center rounded-full"
        style={{
          background: first ? meta.color : "#0B1324",
          border: `2px solid ${meta.color}`,
          boxShadow: first ? `0 0 12px ${meta.color}66` : "none",
        }}
        aria-hidden
      />
      <div
        className="rounded-xl border p-5 transition hover:translate-x-[2px]"
        style={{
          borderColor: "rgba(255,255,255,0.08)",
          background:  "rgba(11,19,36,0.8)",
        }}
      >
        <div className="flex flex-wrap items-center gap-3 text-[11px] qt-mono" style={{ letterSpacing: "0.12em" }}>
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1"
            style={{
              border: `1px solid ${meta.color}`,
              background: `${meta.color}1F`,
              color: meta.color,
              textTransform: "uppercase",
            }}
            data-testid="whatsnew-entry-tag"
          >
            <Icon size={11} /> {meta.label}
          </span>
          <span
            className="inline-flex items-center gap-1.5"
            style={{ color: "var(--qt-text-3)", textTransform: "uppercase" }}
          >
            <Calendar size={11} /> {entry.date || "—"}
          </span>
        </div>

        <h2
          className="mt-3 text-lg font-semibold leading-tight"
          style={{ color: "#FFFFFF" }}
          data-testid="whatsnew-entry-title"
        >
          {entry.title}
        </h2>

        <p
          className="mt-2 text-sm"
          style={{ color: "var(--qt-text-2)", lineHeight: 1.7 }}
          data-testid="whatsnew-entry-summary"
        >
          {entry.summary}
        </p>

        {entry.url && (
          <a
            href={entry.url}
            className="mt-3 inline-flex items-center gap-1.5 text-xs qt-mono hover:opacity-80"
            style={{
              color: meta.color,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
            data-testid="whatsnew-entry-link"
          >
            Try it <ExternalLink size={11} />
          </a>
        )}
      </div>
    </li>
  );
}
